import { useCallback, useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { isEqual, sortBy } from "lodash"
import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { BottomSheetHeights, useApplicationDispatchContext } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadworksDispatchContext, useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { useRoadworkUpdate } from "../../hooks/mutations/roadwork/useRoadworkUpdate"
import { roadworksQueryKeys } from "../../hooks/queries/queryKeys"
import { useRoadworkWithTasks } from "../../hooks/queries/useRoadwork"
import { useTaskTypes } from "../../hooks/queries/useTaskType"
import { sortTasksById } from "../../utils/roadworkUtils"

import { RoadworkDetailsForm } from "./RoadworkDetailsForm"
import { RoadworkDetailsFormError } from "./RoadworkDetailsForm.error"
import { mapRoadworkForForm, useSaveRoadworkTasks } from "./RoadworkDetailsForm.helpers"
import { RoadworkDetailsFormSkeleton } from "./RoadworkDetailsForm.skeleton"

export function RoadworkDetailsFormPost(props) {

    const { className } = props
    const { road, roadworkUuid } = useParams()
    const { data: roadwork, isLoading, isError } = useRoadworkWithTasks(roadworkUuid, road)
    const { currency } = useRoadworksStateContext()
    const { setHasPendingChange } = useRoadworksDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const { addBlockingAlert, cancelBlockingAlert } = useAlertsDispatchContext()
    const [orderedTaskIds, setOrderedTaskIds] = useState(null)
    const queryClient = useQueryClient()
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()
    const isMobile = useIsMobileDevice()
    const [validatedState, setValidatedState] = useState(null)

    const updateValidatedState = useCallback((newRoadwork) => {
        const roadworkTaskIds = newRoadwork?.tasks.map(task => task.uuid || task)
        setOrderedTaskIds(roadworkTaskIds)

        setValidatedState(previousState => {
            const previousValidatedState = previousState ?? newRoadwork

            const roadworkWithOrderedTask = {
                ...previousValidatedState,
                tasks: sortTasksById(newRoadwork.tasks, roadworkTaskIds),
            }
            const roadworkForForm = mapRoadworkForForm(roadworkWithOrderedTask)

            if (isMobile) {
                setCurrentBottomSheetHeight(BottomSheetHeights.HALF)
            }
            setValidatedState({ ...roadworkForForm })

        })
    }, [isMobile, setCurrentBottomSheetHeight])

    const { saveRoadworkTasks } = useSaveRoadworkTasks(updateValidatedState, {
        successSnackbarMessage: t(I18N_NAMESPACE, "components.roadworkDetailsForm.snackbar.success"),
        errorSnackbarMessage: t(I18N_NAMESPACE, "components.roadworkDetailsForm.snackbar.error"),
    })

    const mutation = useRoadworkUpdate(roadwork, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "components.roadworkDetailsForm.snackbar.error"),
        successSnackbarDuration: 10000,
        successSnackbarMessage: t(I18N_NAMESPACE, validatedState?.year <= new Date().getFullYear() ? "components.roadworkDetailsForm.snackbar.success" :
            "components.roadworkDetailsForm.snackbar.yearChanged.success"),
    })

    const {
        data: taskTypes,
        isLoading: isLoadingTaskTypes,
        isError: isErrorTaskTypes,
    } = useTaskTypes()

    const saveRoadwork = useCallback(async (values, otherFields = {}) => {
        await saveRoadworkTasks(values, roadwork)

        const updatedFields = otherFields
        if (validatedState && validatedState?.year !== roadwork?.year) {
            updatedFields.year = validatedState.year
        }

        if (Object.keys(updatedFields).length) {
            mutation.mutate(updatedFields)
        }
        if (isMobile) {
            setCurrentBottomSheetHeight(BottomSheetHeights.HALF)
        }
    }, [isMobile, mutation, roadwork, setCurrentBottomSheetHeight, validatedState, saveRoadworkTasks])

    const onSubmit = useCallback(async (values) => {
        await saveRoadwork(values)
    }, [saveRoadwork])

    useEffect(() => {
        if (roadwork && !validatedState) {
            if (isMobile) {
                setCurrentBottomSheetHeight(BottomSheetHeights.FULL)
            }
            updateValidatedState(roadwork)
        }
    }, [isLoading, isMobile, orderedTaskIds, validatedState, roadwork, setCurrentBottomSheetHeight, updateValidatedState])

    const hasPendingChange = useMemo(() => {
        if (!roadwork || !validatedState) {
            return false
        }

        const {
            tasks: mappedRoadworkTasks,
            ...mappedRoadwork
        } = mapRoadworkForForm(roadwork)

        const {
            tasks: validatedTasks,
            ...validatedRoadwork
        } = validatedState

        const sortedMappedRoadworkTasks = sortBy(mappedRoadworkTasks, t => t.uuid)
        const sortedValidatedTasks = sortBy(validatedTasks, t => t.uuid)
        const validatedTasksHaveTaskType = validatedTasks.every(v => !!v.taskType)
        return validatedTasksHaveTaskType &&
            (!isEqual(mappedRoadwork, validatedRoadwork) ||
                !isEqual(sortedMappedRoadworkTasks, sortedValidatedTasks))
    }, [roadwork, validatedState])

    const editionCleanup = useCallback(() => {
        const sectionQueryKey = roadworksQueryKeys.section(roadworkUuid)
        queryClient.setQueryData(sectionQueryKey, oldSection => {
            return ({
                ...oldSection,
                properties: {
                    ...oldSection.properties,
                    technique: roadwork.technique,
                },
            })
        })
    }, [roadwork, roadworkUuid, queryClient])

    const isRoadworkCreation = useMemo(() => {
        return roadwork && !roadwork.tasks.length
    }, [roadwork])

    useEffect(() => {
        setHasPendingChange(hasPendingChange)
    }, [hasPendingChange, setHasPendingChange])

    useEffect(() => {
        if (hasPendingChange || isRoadworkCreation) {
            addBlockingAlert({
                alertId: "blocking-alert-editing-roadwork-task",
                alertTitle: t(I18N_NAMESPACE, "containers.roadworkTask.blockingAlert.title"),
                cancelText: t(I18N_NAMESPACE, "containers.roadworkTask.blockingAlert.cancelButton"),
                confirmText: t(I18N_NAMESPACE, "containers.roadworkTask.blockingAlert.confirmButton"),
                severity: "warning",
                callback: editionCleanup,
            })
        } else {
            cancelBlockingAlert("blocking-alert-editing-roadwork-task")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addBlockingAlert, cancelBlockingAlert, hasPendingChange, isRoadworkCreation, t])


    if (isError || isErrorTaskTypes) {
        return <RoadworkDetailsFormError className={className} />
    }

    if (isLoading || isLoadingTaskTypes || !currency?.symbol || !validatedState) {
        return <RoadworkDetailsFormSkeleton className={className} />
    }

    return <RoadworkDetailsForm
        className={className}
        currency={currency?.symbol}
        hasPendingChange={hasPendingChange}
        onSubmit={onSubmit}
        onChange={setValidatedState}
        initialState={validatedState}
        roadwork={roadwork}
        taskTypes={taskTypes}
    />
}

RoadworkDetailsFormPost.propTypes = {
    className: PropTypes.string,
}
